import { __PLANET_MEDIA_PLAYER_VERSION__ } from '../constants';
import { Environment } from '../environment';
import {
    DateTimeUtils,
    InstanceManager,
    InstanceManagerEvents,
    PlanetMediaPlayer,
    PlayerCreateEventArgs,
    PlayerDestroyEventArgs,
    PlayerErrorArgs,
    PlayerStateChangeArgs,
} from '../media-player';
import { PlanetMediaPlayerEvents } from '../models';
import { VideoAnalyticsPlugin, VideoAnalyticsTrackerType } from '../plugin';
import {
    EventEnvelop,
    EventKey,
    EventStream,
    Guid,
    IContextSnapshot,
    INavigationMetadata,
    ISpanSnapshot,
    NavigationMetadataProvider,
    OpenTabsCounter,
} from '../utils';
import { BrowsingSession } from './browsing-session';
import { ClientInfo } from './client-info';
import { DocumentEvents, SpanTypes, WindowEvents } from './enums';
import { IUserData } from './i-user-data';
import { NavigationMetadata } from './model';
import { PageSession } from './page-session';
import { AnalyticsTransport, BulkError } from './transport';

let commit_id = 0;

export class AnalyticsAgent {

    private readonly accountCode: string;
    private readonly eventApiUrl: string;
    private readonly token: string = '';

    private traceId: string;

    private clientContext: EventStream;
    private navigationContext: EventStream;

    private userData: IUserData;
    private client: ClientInfo;
    private pageSession: PageSession;
    private browsingSession: BrowsingSession;
    private metadata: INavigationMetadata;

    private transport: AnalyticsTransport;
    private commitTask: number = null;
    private workerTask: number = null;
    private syncTimeout: number = 10000;
    private stopped: boolean = false;

    private consecutiveErrors: number = 0;
    private maxRetries: number = 5;
    private trackingErrors: boolean = false;
    private trackingVisibilityEvents: boolean = false;
    private trackingPlayerEvents: boolean = false;
    private trackingUnloadEvents: boolean = false;

    private readonly frequency: number;
    private readonly commitTimeout: number = 250;

    pageContext: EventStream;

    constructor(accountCode: string, eventApiUrl: string, token: string, frequency: number = 1 / 10) {
        this.accountCode = accountCode;
        this.eventApiUrl = eventApiUrl;
        this.token = token;
        this.frequency = frequency;
    }

    setup(): void {
        const traceId: string = Guid.v1();
        const provider: NavigationMetadataProvider = NavigationMetadataProvider.GetInstance();
        const result: NavigationMetadata = provider.init(); // get session data from cookies / localStorage

        this.traceId = traceId;
        this.metadata = provider.navigationMetadata;
        this.client = this.getClientInfo(this.metadata.clientId);
        this.browsingSession = this.getBrowsingSessionData(this.metadata.browsingSessionId);
        this.pageSession = this.getPageSessionData(this.metadata.pageSessionId, this.metadata.browsingSessionId);

        this.transport = new AnalyticsTransport({
            traceId: this.traceId,
            baseUrl: this.eventApiUrl,
            frequency: this.frequency,
        });

        // event streams
        this.createStreams();
        this.trackBrowsingSession(result);
        // event listeners
        this.trackErrors();
        this.trackPlayerEvents();
        this.trackVisibilityEvents();
        this.trackUnloadEvents();

        this.startWorker();
    }

    trackNavigationEvent(): void {
        this.pageContext.end();
        this.pageContext.flush(true);

        this.createStreams();
        this.trackErrors();
        this.trackBrowsingSession(NavigationMetadataProvider.GetInstance().init());
        this.trackPlayerEvents();
        this.trackVisibilityEvents();
        this.trackUnloadEvents();
        this.pageContext.flush(true);
    }

    private createStreams(): void {

        this.clientContext = new EventStream(SpanTypes.Client, this.client.id, {
            trace: {id: this.traceId},
            account: {id: this.accountCode},
            client: this.client,
        });

        this.clientContext.on('flush', (e) => {
            e = e || {};
            this.scheduleCommit(e.force || false);
        });
        this.clientContext.on('timeout', () => {
            console.log('context timeout');
        });

        if (this.userData) {
            this.clientContext.inject({user: {...this.userData}});
        }

        this.navigationContext = this.clientContext.createStream(SpanTypes.Navigation, this.browsingSession.id, {
            navigation: {id: this.browsingSession.id},
        });

        this.pageContext = this.navigationContext.createStream(SpanTypes.Page, this.pageSession.id, {
            page: {
                isFrame: Environment.Features.frame,
                referrer: document.referrer,
            },
        });
    }

    async commit(): Promise<void> {
        const messages: IContextSnapshot = this.createRequest();
        try {
            await this.transport.postBulkRequest(messages);
            this.clientContext.clear();
            this.consecutiveErrors = 0;
        } catch (e) {
            if (typeof e === 'object' && e instanceof BulkError) {
                if (!(e.isRecoverable) || (this.consecutiveErrors > this.maxRetries)) {
                    this.stop();
                }
                this.consecutiveErrors++;
            }
        }
    }

    beacon(): void {
        this.cancelCommit();
        this.transport.beacon(this.createRequest());
    }

    createRequest(): IContextSnapshot {
        const snapshot: ISpanSnapshot = this.clientContext.getSnapshot();
        const iterator: Iterator<{ stream: EventStream, event: EventEnvelop }, void> = this.clientContext.getEvents();
        let result: IteratorResult<{ stream: EventStream, event: EventEnvelop }, void> = iterator.next();
        let size: number = 0;
        while (result.value) {
            size++;
            result = iterator.next();
        }
        return {
            id: (++commit_id),
            size: size,
            span: snapshot,
            // TODO: use milliseconds from epoch?
            timestamp: DateTimeUtils.UTCNow,
        };
    }

    stop(): void {
        this.cancelCommit();
        if (typeof this.workerTask === 'number') {
            self.clearInterval(this.workerTask);
        }
        this.stopped = true;
    }

    // debounced commit by 250ms
    scheduleCommit(force: boolean = false): void {
        this.cancelCommit();
        if (force === true) {
            this.commit().catch(e => console.error(e));
        } else {
            if (!this.stopped) {
                this.commitTask = self.setTimeout(async () => {
                    await this.commit();
                }, this.commitTimeout);
            }
        }
    }

    startWorker(): void {
        if (!this.stopped) {
            this.workerTask = self.setTimeout(() => {
                this.scheduleCommit(false);
                this.startWorker();
            }, this.syncTimeout);
        }
    }

    cancelCommit(): void {
        if (this.commitTask !== null) {
            self.clearTimeout(this.commitTask);
            this.commitTask = null;
        }
    }

    trackErrors(): boolean {
        if (this.trackingErrors) {
            return false;
        }

        window.onerror = (message: Event | string, file: string, line: number, col: number, error: Error) => {
            this.pageContext.error(error);
        };

        window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
            this.pageContext.error(new Error(e.reason));
        });

        this.trackingErrors = true;
    }

    trackPlayerEvents(): boolean {
        if (this.trackingPlayerEvents) {
            return false;
        }
        InstanceManager.on(InstanceManagerEvents.PlayerCreate, (event: PlayerCreateEventArgs) => {
            const player: PlanetMediaPlayer = event.player;
            const playerId: string = player.getId();

            const playerContext: EventStream = this.pageContext.createStream(SpanTypes.Player, playerId, {
                player: {
                    id: playerId,
                    state: player.getState(),
                    version: __PLANET_MEDIA_PLAYER_VERSION__,
                },
            }).start();

            playerContext.append(EventKey.PlayerInstanceCreated, {
                playerId: playerId,
                version: player.version,
                pageSessionId: this.pageSession.id,
            });

            playerContext.on('beforeRemove', node => console.log('removing', node.id));

            player.on(PlanetMediaPlayerEvents.StateChange, (args: PlayerStateChangeArgs) => {
                playerContext.inject({player: {state: args.newState}});
                playerContext.append(EventKey.PlayerInstanceStateChanged, {
                    playerId: playerId,
                    newState: args.newState,
                    oldState: args.oldState,
                });
            });

            player.on(PlanetMediaPlayerEvents.SetupError, (error: PlayerErrorArgs) => {
                playerContext.append(EventKey.PlayerInstanceSetupError, {
                    playerId: playerId,
                    error: {
                        name: error.code || 0,
                        message: error.message || 'Unknown playback error',
                    },
                });
            });

            player.on(PlanetMediaPlayerEvents.Error, (error: PlayerErrorArgs) => {
                playerContext.append(EventKey.PlayerInstancePlaybackError, {
                    playerId: playerId,
                    error: {
                        name: error.code || 0,
                        message: error.message || 'Unknown playback error',
                    },
                });
            });

            const plugin: VideoAnalyticsPlugin = new VideoAnalyticsPlugin();
            plugin.setup(player, {
                trackers: [
                    {type: VideoAnalyticsTrackerType.Garavot},
                ],
            });
        });

        InstanceManager.on(InstanceManagerEvents.PlayerDestroy, (event: PlayerDestroyEventArgs) => {
            const playerId: string = event.player.getId();
            const stream: EventStream = EventStream.getStream(SpanTypes.Player, playerId);
            if (stream) {
                stream.append(EventKey.PlayerInstanceDestroyed, {
                    pageSessionId: this.pageSession.id,
                    playerId: playerId,
                });
                stream.end();
            }
        });
        this.trackingPlayerEvents = true;
    }

    trackBrowsingSession(sessionData: NavigationMetadata): void {
        const resumeClientContext: boolean = !sessionData.isNewClient;
        this.clientContext.start(resumeClientContext);

        if (sessionData.isNewClient) {
            this.clientContext.append(EventKey.ClientSessionStarted, {
                client: this.client,
            });
        } else {
            this.clientContext.append(EventKey.ClientSessionResumed, {
                clientId: this.client.id,
            });
        }

        this.pageContext.start();
        this.pageContext.append(EventKey.PageSessionStarted, {
            pageSession: this.pageSession,
        });

        this.navigationContext.start(!sessionData.isNewVisit);
        if (sessionData.isNewVisit) {
            this.navigationContext.append(EventKey.BrowsingSessionStarted, {
                browsingSession: this.browsingSession,
                pageUrl: location.href,
            });
        } else {
            this.navigationContext.append(EventKey.BrowsingSessionResumed, {
                browsingSessionId: this.browsingSession.id,
                pageUrl: location.href,
            });
        }
    }

    trackVisibilityEvents(): void {
        if (this.trackingVisibilityEvents) {
            return;
        }
        document.addEventListener(DocumentEvents.VisibilityChange, async () => {
            const visible: boolean = !document.hidden;
            await this.pageContext.append(EventKey.PageVisibilityChanged, {
                isVisible: visible,
            });
        });
        this.trackingVisibilityEvents = false;
    }

    trackUnloadEvents(): boolean {
        if (this.trackingUnloadEvents) {
            return false;
        }
        window.addEventListener(WindowEvents.BeforeUnload, () => {
            const isLastTab: boolean = OpenTabsCounter.count() <= 0;
            if (isLastTab) {
                this.navigationContext.end();
                this.clientContext.end();
            }
            InstanceManager.players.forEach(p => EventStream.getStream(SpanTypes.Player, p.getId()).end());
            this.pageContext.end();
            this.beacon();
        });
        this.trackingUnloadEvents = true;
    }

    getPageSessionData(id: string, browsingSessionId: string): PageSession {
        const session: PageSession = new PageSession(id, browsingSessionId, document.title, location.href, location.hostname, location.search);
        session.referrer = document.referrer ?? '';
        return session;
    }

    getBrowsingSessionData(sessionId: string): BrowsingSession {
        const session: BrowsingSession = new BrowsingSession(sessionId);
        session.clientId = this.client.id;
        return session;
    }

    getClientInfo(clientId: string): ClientInfo {
        return new ClientInfo(clientId);
    }
}
